import { Button, cn, Select, type SelectOption } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  PlusIcon,
  ProhibitIcon,
  TrashIcon,
  UserPlusIcon,
  UsersThreeIcon,
  XIcon,
} from "@phosphor-icons/react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass as inputClass } from "@/lib/utils/cell-input";
import {
  stickyHeadSecondRowClass,
  stickyRowHeaderContentClass,
  useScrollEndSpacer,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import { fetchAllCompanies } from "./components";
import type { Team, TeamMember } from "./contract-teams-drawer";
import {
  type ContractGroup,
  ContractGroupsList,
  ContractsPanelFrame,
  ContractsSectionTitle,
  contractsSaveMessage,
  contractsTableFrameClass,
  DeactivatedLabel,
  FormerReferent,
  contractsHeadCellClass as headCellClass,
  contractsHeadFirstRowCellClass as headFirstRowCellClass,
  contractsHeadGroupCellClass as headGroupCellClass,
  historyRemover,
  notifyDraftSaved,
  personName,
  ReferentCell,
  type ReferentHistoryEntry,
  type ReferentHistoryRemoval,
  refusal,
  replacementDay,
  revealOnMount,
  contractsTeamsSeparatorClass as teamsSeparatorClass,
  useContractGroupsDraft,
  useDraftBaseline,
} from "./contracts-panel";

export type Referent = {
  id: number;
  employeeId: string;
  mail: string | null;
  function: string | null;
  /** False once deactivated, also when deactivated in their company. */
  isActive: boolean;
  employee: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    companyId: number;
  } | null;
};

export type WorkContractRow = {
  id: number;
  type: "travaux_n_1" | "travaux_n_2";
  shortName: string | null;
  isActive: boolean;
  groupId: number | null;
  customerId: number | null;
  /** Rang 2 only: the rang 1 contract it is sub-contracted from. */
  parentContractId: number | null;
  /** Assigned to an intervention, directly or through a team. */
  hasInterventions: boolean;
  performer: { id: number; name: string };
  customer: { id: number; name: string } | null;
  group: { id: number; name: string; shortName: string | null } | null;
  referent: Referent | null;
  referentHistory: ReferentHistoryEntry[];
  teams: Team[];
};

/**
 * Editable fields of a saved contract. Changing the contracting company
 * (performer) removes the contract's referent and team members on save; it
 * is locked once the yard is in service.
 */
type ContractDraft = {
  performerId: number;
  shortName: string;
  groupId: number | null;
  /** Rang 1: the yard's MOA (fixed). */
  customerId: number | null;
  /** Rang 2: the rang 1 contract it is sub-contracted from. */
  parentContractId: number | null;
};

type NewContractRow = {
  tempId: string;
  rang: 1 | 2;
} & ContractDraft;

type NewContractPatch = Partial<Omit<NewContractRow, "tempId" | "rang">>;

/**
 * Contract tables stacked in the panel share these widths (PO, N65: « figer
 * les largeurs de colonnes des tableaux contrats rang 1 et rang 2 »). The
 * browser stretches them to the table's width: `donneurOrdre` plus
 * `referentWithoutGroupement` must add up to `referent` plus `groupement`, or
 * the sous-traitance columns would stretch differently.
 */
const columnWidths = {
  designation: "calc(20ch + 76px)",
  abbreviation: "calc(8ch + 28px)",
  referent: "calc(16ch + 60px)",
  groupement: "calc(14ch + 56px)",
  team: "calc(12ch + 40px)",
  members: "calc(20ch + 12px)",
  donneurOrdre: "52px",
  referentWithoutGroupement: "calc(30ch + 64px)",
};

function generateTempId(): string {
  return `tmp-${Math.random().toString(36).slice(2)}`;
}

export const employeeName = (referent: Referent): string => {
  const employee = referent.employee;
  if (isNil(employee)) return referent.employeeId;
  return (
    `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim() ||
    referent.employeeId
  );
};

const companyLockedTitle =
  "Chantier en service : la société d'un contrat ne peut plus être changée. Interrompez ce contrat, puis ajoutez celui de la nouvelle société.";

const addContractsClosedTitle =
  "Chantier interrompu, archivé ou clôturé : on ne peut plus y ajouter de contrat.";

const memberName = (member: TeamMember): string => {
  const employee = member.employee;
  if (isNil(employee)) return member.employeeId;
  return (
    `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim() ||
    member.employeeId
  );
};

/** A refetch of `contracts` drops the draft. */
export type WorkContractsEditablePanelProps = {
  yardId: number;
  contracts: WorkContractRow[];
  groups: ContractGroup[];
  /** Shows the groupements above the contracts (toggled in `toolbar`). */
  groupsExpanded: boolean;
  /** Says which groups the screen leaves out, and why. */
  groupsNote?: string;
  onDeleteGroup: (group: ContractGroup) => void;
  yardMoaCompany: { id: number; name: string } | null;
  /** Once in service, a contract's company can no longer change (PO 30/10, N72). */
  isCommissioned: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSuccess: () => Promise<void>;
  onManageTeams: (contract: WorkContractRow) => void;
  onSetReferent: (contract: WorkContractRow) => void;
  onRemoveReferent: (contract: WorkContractRow) => void;
  /** After mise en service: the referent is deactivated (and replaced), not removed. */
  onDeactivateReferent?: (contract: WorkContractRow) => void;
  /** The « x » of a former referent: removes them from the history. */
  onDeleteHistory?: (removal: ReferentHistoryRemoval) => void;
  onInterrupt: (contract: WorkContractRow) => void;
  onDelete: (contract: WorkContractRow) => void;
  /**
   * A rang 1 contract can only be deleted while the yard is in preparation;
   * afterwards it is interrupted instead.
   */
  canDeleteRang1: boolean;
  /** Contracts are only added while the yard is in preparation or in service. */
  canAddContracts: boolean;
  /** Left part of the panel's fixed top bar (e.g. the groupements toggle). */
  toolbar?: ReactNode;
};

export function WorkContractsEditablePanel({
  yardId,
  contracts,
  groups,
  groupsExpanded,
  groupsNote,
  onDeleteGroup,
  yardMoaCompany,
  isCommissioned,
  onDirtyChange,
  onSuccess,
  onManageTeams,
  onSetReferent,
  onRemoveReferent,
  onDeactivateReferent,
  onDeleteHistory,
  onInterrupt,
  onDelete,
  canDeleteRang1,
  canAddContracts,
  toolbar,
}: WorkContractsEditablePanelProps) {
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [draftContracts, setDraftContracts] = useState<
    Map<number, ContractDraft>
  >(new Map());
  const [newRows, setNewRows] = useState<NewContractRow[]>([]);
  const groupsDraft = useContractGroupsDraft(groups);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCompanies = useCallback(async () => {
    const { companies } = await fetchAllCompanies();
    if (isNotNil(companies)) {
      setCompanyOptions(
        companies.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      );
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const rang1 = contracts.filter((c) => c.type === "travaux_n_1");
  const rang2 = contracts.filter((c) => c.type === "travaux_n_2");

  const groupOptions: SelectOption[] = [
    { label: "Aucun groupement", value: "0" },
    ...groupsDraft.groups.map((g) => ({ label: g.name, value: String(g.id) })),
  ];
  const contractLabel = (contract: WorkContractRow) =>
    isNotNil(contract.shortName)
      ? `${contract.performer.name} (${contract.shortName})`
      : contract.performer.name;

  // Donneur d'ordre of a rang 2 contract: an active rang 1 contract on this
  // chantier. The current parent stays listed even once interrupted, flagged
  // as such, until the rang 2 contract is moved to the rang 1 contract taking
  // over.
  const parentOptions = (currentParentId: number | null): SelectOption[] => [
    { label: "Aucun", value: "0" },
    ...rang1
      .filter((c) => c.isActive || c.id === currentParentId)
      .map((c) => ({
        label: c.isActive
          ? contractLabel(c)
          : `${contractLabel(c)} (interrompu)`,
        value: String(c.id),
      })),
  ];

  const isParentInterrupted = (contract: WorkContractRow) =>
    rang1.some((c) => c.id === contract.parentContractId && !c.isActive);

  // Why the API would refuse the deletion, shown on the greyed-out button.
  const deleteRefusal = (contract: WorkContractRow): string | undefined => {
    if (contract.type === "travaux_n_1" && !canDeleteRang1) {
      return "Un contrat rang 1 ne peut être supprimé que pendant la période de préparation du chantier : interrompez-le plutôt.";
    }
    if (isCommissioned && contract.hasInterventions) {
      return "Ce contrat est affecté à des interventions sur un chantier mis en service : il ne peut pas être supprimé.";
    }
    return undefined;
  };

  // Sous-traitance contracts are stacked one table per donneur d'ordre — the
  // rang 1 contract they are sub-contracted from — instead of a single
  // flat table with a column for it. Groupement doesn't apply to rang 2,
  // so it's dropped entirely.
  const [newParentContractId, setNewParentContractId] = useState<number | null>(
    null,
  );

  const parentTitle = (parentContractId: number | null): string => {
    if (isNil(parentContractId)) return "Sans donneur d'ordre";
    const parent = rang1.find((c) => c.id === parentContractId);
    if (isNil(parent)) return "Donneur d'ordre inconnu";
    return parent.isActive
      ? contractLabel(parent)
      : `${contractLabel(parent)} (interrompu)`;
  };

  // Same label, without the abbreviation: used where the donneur d'ordre
  // (rang 1, sous-traitance) is shown as a group label rather than picked
  // from a list — the abbreviation doesn't help identify it there.
  const parentTitlePlain = (parentContractId: number | null): string => {
    if (isNil(parentContractId)) return "Sans donneur d'ordre";
    const parent = rang1.find((c) => c.id === parentContractId);
    if (isNil(parent)) return "Donneur d'ordre inconnu";
    return parent.isActive
      ? parent.performer.name
      : `${parent.performer.name} (interrompu)`;
  };

  const rang2ParentIds = (() => {
    const ids = new Set<number | null>();
    for (const c of rang2) ids.add(c.parentContractId);
    for (const row of newRows) {
      if (row.rang === 2) ids.add(row.parentContractId);
    }
    return [...ids].sort((a, b) => {
      if (isNil(a)) return 1;
      if (isNil(b)) return -1;
      return parentTitle(a).localeCompare(parentTitle(b), "fr");
    });
  })();

  // Rows without a donneur d'ordre, or whose donneur d'ordre was interrupted,
  // keep an inline selector so they can be attached to the rang 1 contract
  // taking over the mission.
  const needsParentSelect = (parentContractId: number | null) =>
    isNil(parentContractId) ||
    rang1.some((c) => c.id === parentContractId && !c.isActive);

  // Groups with a resolved, active donneur d'ordre merge into one combined
  // table — no column to carry per group, so no
  // need for a per-group header; "sans donneur d'ordre" / interrupted groups
  // keep their own table and selector (badge "À reprendre").
  const mergeableParentIds = rang2ParentIds.filter(
    (id): id is number => isNotNil(id) && !needsParentSelect(id),
  );
  const specialParentIds = rang2ParentIds.filter(needsParentSelect);

  // Excludes rang 1 contracts already a donneur d'ordre: a table for them
  // already exists, picked through its own
  // "Ajouter" instead of through this "nouveau donneur d'ordre" selector.
  const newParentOptions: SelectOption[] = rang1
    .filter((c) => c.isActive && !rang2ParentIds.includes(c.id))
    .map((c) => ({ label: contractLabel(c), value: String(c.id) }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));

  const serverDraft = (contract: WorkContractRow): ContractDraft => ({
    performerId: contract.performer.id,
    shortName: contract.shortName ?? "",
    groupId: contract.groupId,
    customerId: contract.customerId,
    parentContractId: contract.parentContractId,
  });

  const getDraft = (contract: WorkContractRow): ContractDraft =>
    draftContracts.get(contract.id) ?? serverDraft(contract);

  const isDirtyRow = (contract: WorkContractRow) =>
    draftContracts.has(contract.id);

  const onFieldChange = (
    contract: WorkContractRow,
    patch: Partial<ContractDraft>,
  ) => {
    setDraftContracts((prev) => {
      const current = prev.get(contract.id) ?? serverDraft(contract);
      const next = { ...current, ...patch };
      const server = serverDraft(contract);
      const updated = new Map(prev);
      const sameAsServer =
        next.performerId === server.performerId &&
        next.shortName === server.shortName &&
        next.groupId === server.groupId &&
        next.customerId === server.customerId &&
        next.parentContractId === server.parentContractId;
      if (sameAsServer) updated.delete(contract.id);
      else updated.set(contract.id, next);
      return updated;
    });
  };

  const addRow = (rang: 1 | 2, parentContractId: number | null = null) => {
    setNewRows((prev) => [
      ...prev,
      {
        tempId: generateTempId(),
        rang,
        performerId: 0,
        shortName: "",
        groupId: null,
        customerId: rang === 1 ? (yardMoaCompany?.id ?? null) : null,
        parentContractId: rang === 2 ? parentContractId : null,
      },
    ]);
  };

  const onNewRowChange = (tempId: string, patch: NewContractPatch) => {
    setNewRows((prev) =>
      prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
    );
  };

  const removeNewRow = (tempId: string) => {
    setNewRows((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const isDirty =
    draftContracts.size > 0 || newRows.length > 0 || groupsDraft.isDirty;

  useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const cancelDraft = () => {
    setDraftContracts(new Map());
    setNewRows([]);
    groupsDraft.reset();
  };

  useDraftBaseline(contracts, () => {
    cancelDraft();
    setNewParentContractId(null);
  });

  const canSave = () => {
    const incomplete =
      newRows.some((row) => !row.performerId) ||
      [...draftContracts.values()].some((d) => !d.performerId);
    if (incomplete) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de sélectionner une société contractante avant d'enregistrer.",
      });
      return false;
    }

    const missingParent = newRows.some(
      (row) => row.rang === 2 && isNil(row.parentContractId),
    );
    if (missingParent) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de sélectionner un donneur d'ordre (rang 1) avant d'enregistrer.",
      });
      return false;
    }
    if (groupsDraft.hasUnnamedGroup) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de saisir le nom de chaque groupement avant d'enregistrer.",
      });
      return false;
    }
    return true;
  };

  const save = async () => {
    setIsSaving(true);
    try {
      await saveDraft();
    } finally {
      setIsSaving(false);
    }
  };

  const saveDraft = async () => {
    const refused: string[] = [];

    for (const [id, d] of draftContracts.entries()) {
      const contract = contracts.find((c) => c.id === id);
      const isRang2 = contract?.type === "travaux_n_2";
      // Rang 2: the API derives the customer from the parent contract, and
      // the parent is only sent when changed so an interrupted one can stay
      // while other fields are edited.
      const parentChanged = contract?.parentContractId !== d.parentContractId;
      const { error } = await apiClient.PATCH("/api/admin/contracts/{id}", {
        params: { path: { id } },
        body: {
          performerId: d.performerId,
          shortName: d.shortName.trim() ? d.shortName.trim() : null,
          groupId: d.groupId,
          ...(isRang2
            ? parentChanged
              ? { parentContractId: d.parentContractId }
              : {}
            : { customerId: d.customerId }),
        },
      });
      if (error) {
        refused.push(
          refusal(`Contrat ${contract?.performer.name ?? id}`, error),
        );
      }
    }

    for (const row of newRows) {
      const { error } = await apiClient.POST("/api/admin/contracts", {
        body: {
          yardId,
          type: row.rang === 1 ? "travaux_n_1" : "travaux_n_2",
          performerId: row.performerId,
          shortName: row.shortName.trim() ? row.shortName.trim() : null,
          groupId: row.groupId,
          ...(row.rang === 2
            ? { parentContractId: row.parentContractId }
            : { customerId: row.customerId }),
        },
      });
      if (error) {
        const name = companyOptions.find(
          (option) => option.value === String(row.performerId),
        )?.label;
        refused.push(refusal(`Nouveau contrat ${name ?? ""}`.trim(), error));
      }
    }

    // After the contracts: a mandataire must already be in its group.
    refused.push(...(await groupsDraft.save()));
    notifyDraftSaved(refused);
    await onSuccess();
  };

  // Title of the "Contrats de sous-traitance" section, pinned above every
  // table stacked in it.
  const subcontractingSticky = useStickyHeaderOffsets();
  const scrollEndSpacerRef = useScrollEndSpacer(groupsExpanded);

  return (
    <ContractsPanelFrame
      toolbar={toolbar}
      isDirty={isDirty}
      isSaving={isSaving}
      canSave={canSave}
      saveMessage={contractsSaveMessage(
        "Les modifications apportées aux contrats travaux vont être enregistrées.",
        contracts,
        draftContracts,
      )}
      onSave={save}
      onDiscard={cancelDraft}
    >
      {groupsExpanded && (
        <ContractGroupsList
          groups={groupsDraft.groups}
          candidatesFor={(group) =>
            rang1
              .filter((c) => getDraft(c).groupId === group.id)
              .map((c) => ({ id: c.id, name: c.performer.name }))
          }
          onRename={(group, patch) => groupsDraft.change(group.id, patch)}
          isDirty={groupsDraft.isGroupDirty}
          onDelete={onDeleteGroup}
          onSetMandataire={(groupId, contractId) =>
            groupsDraft.change(groupId, { mandataireContractId: contractId })
          }
          note={groupsNote}
        />
      )}

      <ContractTable
        rang={1}
        stickyTitle
        title="Contrats travaux (rang 1)"
        rows={rang1}
        newRows={newRows.filter((r) => r.rang === 1)}
        companyOptions={companyOptions}
        companyLocked={isCommissioned}
        groupOptions={groupOptions}
        showGroupement
        showParentSelect={false}
        parentOptions={parentOptions}
        isParentInterrupted={isParentInterrupted}
        getDraft={getDraft}
        isDirtyRow={isDirtyRow}
        onFieldChange={onFieldChange}
        onNewRowChange={onNewRowChange}
        onRemoveNewRow={removeNewRow}
        onAddRow={canAddContracts ? () => addRow(1) : undefined}
        onManageTeams={onManageTeams}
        onSetReferent={onSetReferent}
        onRemoveReferent={onRemoveReferent}
        onDeactivateReferent={onDeactivateReferent}
        onDeleteHistory={onDeleteHistory}
        onInterrupt={onInterrupt}
        onDelete={onDelete}
        deleteRefusal={deleteRefusal}
        emptyLabel="Aucun contrat travaux de rang 1"
        moaLabel={yardMoaCompany?.name ?? null}
      />

      <section style={subcontractingSticky.style}>
        <ContractsSectionTitle
          title="Contrats de sous-traitance"
          stickyRef={subcontractingSticky.titleRef}
        />

        {rang2ParentIds.length === 0 ? (
          <div className="flex min-h-[100px] items-center justify-center rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700">
            Aucun contrat sous-traité
          </div>
        ) : (
          <div className="space-y-4">
            {mergeableParentIds.length > 0 && (
              <SubcontractorsTable
                groups={mergeableParentIds.map((parentContractId) => ({
                  parentContractId,
                  title: parentTitlePlain(parentContractId),
                  rows: rang2.filter(
                    (c) => c.parentContractId === parentContractId,
                  ),
                  newRows: newRows.filter(
                    (r) =>
                      r.rang === 2 && r.parentContractId === parentContractId,
                  ),
                }))}
                companyOptions={companyOptions}
                companyLocked={isCommissioned}
                getDraft={getDraft}
                isDirtyRow={isDirtyRow}
                onFieldChange={onFieldChange}
                onNewRowChange={onNewRowChange}
                onRemoveNewRow={removeNewRow}
                onAddRow={
                  canAddContracts
                    ? (parentContractId) => addRow(2, parentContractId)
                    : undefined
                }
                onManageTeams={onManageTeams}
                onSetReferent={onSetReferent}
                onRemoveReferent={onRemoveReferent}
                onDeactivateReferent={onDeactivateReferent}
                onDeleteHistory={onDeleteHistory}
                onInterrupt={onInterrupt}
                onDelete={onDelete}
                deleteRefusal={deleteRefusal}
              />
            )}

            {/* Sans donneur d'ordre, ou donneur d'ordre interrompu : gardent
                  un tableau à part avec leur sélecteur dédié (badge « À
                  reprendre »), pas de société unique à afficher en cellule
                  verticale. */}
            {specialParentIds.map((parentContractId) => (
              <ContractTable
                key={parentContractId ?? "none"}
                rang={2}
                title={parentTitlePlain(parentContractId)}
                rows={rang2.filter(
                  (c) => c.parentContractId === parentContractId,
                )}
                newRows={newRows.filter(
                  (r) =>
                    r.rang === 2 && r.parentContractId === parentContractId,
                )}
                companyOptions={companyOptions}
                companyLocked={isCommissioned}
                groupOptions={groupOptions}
                showGroupement={false}
                showParentSelect={needsParentSelect(parentContractId)}
                parentOptions={parentOptions}
                isParentInterrupted={isParentInterrupted}
                getDraft={getDraft}
                isDirtyRow={isDirtyRow}
                onFieldChange={onFieldChange}
                onNewRowChange={onNewRowChange}
                onRemoveNewRow={removeNewRow}
                onAddRow={
                  canAddContracts
                    ? () => addRow(2, parentContractId)
                    : undefined
                }
                onManageTeams={onManageTeams}
                onSetReferent={onSetReferent}
                onRemoveReferent={onRemoveReferent}
                onDeactivateReferent={onDeactivateReferent}
                onDeleteHistory={onDeleteHistory}
                onInterrupt={onInterrupt}
                onDelete={onDelete}
                deleteRefusal={deleteRefusal}
                emptyLabel="Aucun contrat sous-traité"
                moaLabel={null}
              />
            ))}
          </div>
        )}

        {canAddContracts && (
          <>
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 p-2 dark:border-zinc-700">
              <span className="shrink-0 text-xs text-zinc-500">
                Nouveau donneur d'ordre :
              </span>
              <Select
                flat
                options={newParentOptions}
                disabled={newParentOptions.length === 0}
                value={
                  isNotNil(newParentContractId)
                    ? [String(newParentContractId)]
                    : []
                }
                onValueChange={(details) => {
                  const val = details.value[0];
                  setNewParentContractId(val ? Number(val) : null);
                }}
                placeholder="Contrat rang 1 donneur d'ordre"
              />
              <Button
                outline
                disabled={isNil(newParentContractId)}
                onClick={() => {
                  if (isNil(newParentContractId)) return;
                  addRow(2, newParentContractId);
                  setNewParentContractId(null);
                }}
              >
                <PlusIcon data-slot="icon" className="size-4" />
                Ajouter un contrat de sous-traitance
              </Button>
            </div>
            {newParentOptions.length === 0 && (
              <p className="mt-1 text-xs text-zinc-500">
                {rang1.some((c) => c.isActive)
                  ? "Tous les contrats rang 1 actifs ont déjà leur tableau de sous-traitance : utilisez le bouton « Ajouter » de ce tableau pour y ajouter un sous-traitant."
                  : "Ajoutez d'abord un contrat travaux de rang 1 actif : chaque contrat de sous-traitance est rattaché à un contrat rang 1, son donneur d'ordre."}
              </p>
            )}
          </>
        )}
        <div ref={scrollEndSpacerRef} aria-hidden />
      </section>
    </ContractsPanelFrame>
  );
}

/**
 * Contracting company of a saved contract, editable in the draft until the
 * yard is in service.
 */
function PerformerSelect({
  options,
  value,
  locked,
  onChange,
}: {
  options: SelectOption[];
  value: number;
  locked: boolean;
  onChange: (performerId: number) => void;
}) {
  const label = options.find((option) => option.value === String(value))?.label;
  if (locked) {
    return (
      <span
        className="min-w-0 flex-1 truncate px-2 py-1.5"
        title={companyLockedTitle}
      >
        {label}
      </span>
    );
  }
  return (
    <div className="min-w-0 flex-1">
      <Select
        flat
        options={options}
        value={value ? [String(value)] : []}
        onValueChange={(details) => {
          const val = details.value[0];
          onChange(val ? Number(val) : 0);
        }}
        placeholder="Société"
      />
    </div>
  );
}

type ContractTableProps = {
  rang: 1 | 2;
  title: string;
  rows: WorkContractRow[];
  newRows: NewContractRow[];
  companyOptions: SelectOption[];
  /** The yard is in service: saved contracts keep their company. */
  companyLocked: boolean;
  groupOptions: SelectOption[];
  /** Rang 1 only — groupements don't apply to sous-traitance contracts. */
  showGroupement: boolean;
  /**
   * Only for the "Sans donneur d'ordre" bucket and for a donneur d'ordre that
   * was interrupted: an inline selector attaches the contracts to an active
   * rang 1 contract. Other tables drop the column entirely — the table's
   * title carries the donneur d'ordre.
   */
  showParentSelect: boolean;
  parentOptions: (currentParentId: number | null) => SelectOption[];
  isParentInterrupted: (contract: WorkContractRow) => boolean;
  getDraft: (contract: WorkContractRow) => ContractDraft;
  isDirtyRow: (contract: WorkContractRow) => boolean;
  onFieldChange: (
    contract: WorkContractRow,
    patch: Partial<ContractDraft>,
  ) => void;
  onNewRowChange: (tempId: string, patch: NewContractPatch) => void;
  onRemoveNewRow: (tempId: string) => void;
  /** Left out when contracts can no longer be added: hides the button. */
  onAddRow?: () => void;
  onManageTeams: (contract: WorkContractRow) => void;
  onSetReferent: (contract: WorkContractRow) => void;
  onRemoveReferent: (contract: WorkContractRow) => void;
  /** After mise en service: the referent is deactivated (and replaced), not removed. */
  onDeactivateReferent?: (contract: WorkContractRow) => void;
  onDeleteHistory?: (removal: ReferentHistoryRemoval) => void;
  onInterrupt: (contract: WorkContractRow) => void;
  onDelete: (contract: WorkContractRow) => void;
  deleteRefusal: (contract: WorkContractRow) => string | undefined;
  emptyLabel: string;
  /** Rang 1 only: the yard's MOA, shown as a note (customer is fixed, not editable). */
  moaLabel: string | null;
  /**
   * Pins the title above the table. Off for the tables stacked in the
   * sous-traitance section: they stay under that section's pinned title.
   */
  stickyTitle?: boolean;
};

function ContractTable({
  rang,
  title,
  rows,
  newRows,
  companyOptions,
  companyLocked,
  groupOptions,
  showGroupement,
  showParentSelect,
  parentOptions,
  isParentInterrupted,
  getDraft,
  isDirtyRow,
  onFieldChange,
  onNewRowChange,
  onRemoveNewRow,
  onAddRow,
  onManageTeams,
  onSetReferent,
  onRemoveReferent,
  onDeactivateReferent,
  onDeleteHistory,
  onInterrupt,
  onDelete,
  deleteRefusal,
  emptyLabel,
  moaLabel,
  stickyTitle = false,
}: ContractTableProps) {
  const { titleRef, headerRef, style } = useStickyHeaderOffsets();
  return (
    <section style={style}>
      <ContractsSectionTitle
        title={title}
        stickyRef={stickyTitle ? titleRef : undefined}
        subtitle={
          rang === 1 && (
            <>
              <p className="text-xs text-zinc-500">
                Client (maître d'ouvrage du chantier) :{" "}
                {moaLabel ?? "aucun maître d'ouvrage défini"}
              </p>
              {companyLocked && (
                <p className="text-xs text-zinc-500">
                  {isNotNil(onAddRow)
                    ? companyLockedTitle
                    : addContractsClosedTitle}
                </p>
              )}
            </>
          )
        }
        action={
          isNotNil(onAddRow) && (
            <Button onClick={onAddRow}>
              <PlusIcon data-slot="icon" className="size-4" />
              Ajouter
            </Button>
          )
        }
      />

      {rows.length === 0 && newRows.length === 0 ? (
        <div className="flex min-h-[100px] items-center justify-center rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700">
          {emptyLabel}
        </div>
      ) : (
        // No overflow wrapper: the header cells pin to the panel's scroll
        // area (`overflow-clip` rounds the corners without creating a scroll
        // container).
        <div className={contractsTableFrameClass}>
          {/*
            Largeurs calculées suivant la méthode de screen-design.md :
            caractères minimum de .agents/screen-column-widths.md (écran 910,
            panneau contrats) + le habillage réel du contrôle de la colonne
            (padding de cellule p-1.5 = 12px ; pour un Select `flat`, + son
            padding interne px-2 = 16px et son chevron 20px + ml-2 8px = 28px ;
            pour l'input Abrév. et le texte Société contractante, + le
            padding interne px-2 = 16px du champ/span ; + les boutons picto
            relogés dans la cellule). Caractères resserrés pour que le
            tableau tienne sans scroll horizontal à pleine largeur du panneau
            (1180px).
          */}
          <table className="w-full table-fixed border-collapse text-xs">
            <colgroup>
              <col style={{ width: columnWidths.designation }} />
              <col style={{ width: columnWidths.abbreviation }} />
              <col style={{ width: columnWidths.referent }} />
              {showGroupement && (
                <col style={{ width: columnWidths.groupement }} />
              )}
              <col style={{ width: columnWidths.team }} />
              <col style={{ width: columnWidths.members }} />
              {showParentSelect && (
                <col style={{ width: columnWidths.groupement }} />
              )}
            </colgroup>
            <thead ref={headerRef}>
              {/* Deux groupes de colonnes : "Société contractante" (Nom /
                  Abréviation / Référent contrat / Groupement) et "Équipes"
                  (Désignation, Membres). Donneur d'ordre ne fait partie
                  d'aucun groupe : il s'étend sur les deux lignes d'en-tête
                  (rowSpan) plutôt que de répéter un groupe vide. Pas de
                  colonne Actions dédiée : les boutons d'action relatifs au
                  contrat vivent dans la cellule Désignation (société), ceux
                  relatifs aux équipes dans la cellule Désignation (équipe). */}
              <tr className="bg-zinc-50 text-left dark:bg-zinc-900">
                <th
                  colSpan={showGroupement ? 4 : 3}
                  className={headGroupCellClass}
                >
                  Société contractante
                </th>
                <th
                  colSpan={2}
                  className={cn(headGroupCellClass, teamsSeparatorClass)}
                >
                  Équipes
                </th>
                {showParentSelect && (
                  <th
                    rowSpan={2}
                    className={cn(headFirstRowCellClass, "align-bottom")}
                  >
                    Donneur d'ordre
                  </th>
                )}
              </tr>
              <tr className="bg-zinc-50 text-left dark:bg-zinc-900">
                <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                  Désignation
                </th>
                <th
                  className={cn(stickyHeadSecondRowClass, headCellClass)}
                  title="Abréviation"
                >
                  Abrév.
                </th>
                <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                  Référent contrat
                </th>
                {showGroupement && (
                  <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                    Groupement
                  </th>
                )}
                <th
                  className={cn(
                    stickyHeadSecondRowClass,
                    headCellClass,
                    teamsSeparatorClass,
                  )}
                >
                  Désignation
                </th>
                <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                  Membres
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((contract) => {
                const draft = getDraft(contract);
                const dirty = isDirtyRow(contract);
                // One row per team — a contract with no team still gets one
                // placeholder row. The société-side columns span every one
                // of a contract's team rows.
                const teamRows: (Team | null)[] =
                  contract.teams.length > 0 ? contract.teams : [null];
                const rowSpan = teamRows.length;
                return teamRows.map((team, index) => (
                  <tr
                    key={team ? `${contract.id}-${team.id}` : contract.id}
                    className={cn(
                      "border-t border-zinc-100 dark:border-zinc-800",
                      dirty && "bg-amber-50 dark:bg-amber-900/20",
                    )}
                  >
                    {index === 0 && (
                      <>
                        <td rowSpan={rowSpan} className="p-1.5 align-top">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <PerformerSelect
                                options={companyOptions}
                                value={draft.performerId}
                                locked={companyLocked}
                                onChange={(performerId) =>
                                  onFieldChange(contract, { performerId })
                                }
                              />
                              {!contract.isActive && (
                                <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                  Interrompu
                                </span>
                              )}
                            </div>
                            <div className="flex shrink-0 gap-1">
                              {contract.isActive && (
                                <Button
                                  plain
                                  onClick={() => onInterrupt(contract)}
                                  aria-label="Interrompre le contrat"
                                >
                                  <ProhibitIcon className="size-4" />
                                </Button>
                              )}
                              <Button
                                plain
                                onClick={() => onDelete(contract)}
                                disabled={isNotNil(deleteRefusal(contract))}
                                aria-label="Supprimer"
                                title={deleteRefusal(contract)}
                              >
                                <TrashIcon className="size-4" />
                              </Button>
                            </div>
                          </div>
                        </td>
                        <td rowSpan={rowSpan} className="p-1.5 align-top">
                          <input
                            type="text"
                            value={draft.shortName}
                            onChange={(e) =>
                              onFieldChange(contract, {
                                shortName: e.target.value,
                              })
                            }
                            maxLength={50}
                            className={inputClass(dirty)}
                            placeholder="Ex: GO"
                          />
                        </td>
                        <td rowSpan={rowSpan} className="p-1.5 align-top">
                          <ReferentCell
                            referent={contract.referent}
                            history={contract.referentHistory}
                            onDeleteHistory={historyRemover(
                              onDeleteHistory,
                              contract.id,
                            )}
                            actions={
                              <ReferentActions
                                contract={contract}
                                onSetReferent={onSetReferent}
                                onRemoveReferent={onRemoveReferent}
                                onDeactivateReferent={onDeactivateReferent}
                              />
                            }
                          />
                        </td>
                        {showGroupement && (
                          <td rowSpan={rowSpan} className="p-1.5 align-top">
                            <Select
                              flat
                              options={groupOptions}
                              value={
                                draft.groupId ? [String(draft.groupId)] : []
                              }
                              onValueChange={(details) => {
                                const val = details.value[0];
                                onFieldChange(contract, {
                                  groupId:
                                    val && val !== "0" ? Number(val) : null,
                                });
                              }}
                              placeholder=""
                            />
                          </td>
                        )}
                      </>
                    )}
                    <td className="border-l-2 border-zinc-300 p-1.5 align-top dark:border-zinc-700">
                      <div className="flex items-center justify-between gap-1">
                        {team ? (
                          <span
                            className="min-w-0 truncate text-xs font-medium text-zinc-700 dark:text-zinc-300"
                            title={team.name}
                          >
                            {team.name}
                          </span>
                        ) : (
                          <span className="text-zinc-400">-</span>
                        )}
                        {index === 0 && (
                          <Button
                            plain
                            onClick={() => onManageTeams(contract)}
                            aria-label="Gérer les équipes"
                            className="shrink-0"
                          >
                            <UsersThreeIcon className="size-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="p-1.5 align-top">
                      {team ? (
                        <TeamMembersCell
                          team={team}
                          onDeleteHistory={historyRemover(
                            onDeleteHistory,
                            contract.id,
                            team.name,
                          )}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    {index === 0 && showParentSelect && (
                      <td rowSpan={rowSpan} className="p-1.5 align-top">
                        <div className="flex items-center gap-2">
                          <Select
                            flat
                            options={parentOptions(contract.parentContractId)}
                            value={[String(draft.parentContractId ?? 0)]}
                            onValueChange={(details) => {
                              const val = details.value[0];
                              onFieldChange(contract, {
                                parentContractId:
                                  val && val !== "0" ? Number(val) : null,
                              });
                            }}
                            placeholder="Donneur d'ordre"
                          />
                          {isParentInterrupted(contract) &&
                            draft.parentContractId ===
                              contract.parentContractId && (
                              <span
                                className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                title="Son donneur d'ordre est interrompu : rattachez ce contrat au contrat rang 1 qui reprend la mission."
                              >
                                À reprendre
                              </span>
                            )}
                        </div>
                      </td>
                    )}
                  </tr>
                ));
              })}

              {newRows.map((row) => (
                <tr
                  key={row.tempId}
                  ref={revealOnMount}
                  className="border-t border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20"
                >
                  <td className="p-1.5 align-top">
                    <div className="flex items-center gap-1">
                      <Select
                        flat
                        options={companyOptions}
                        value={row.performerId ? [String(row.performerId)] : []}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          onNewRowChange(row.tempId, {
                            performerId: val ? Number(val) : 0,
                          });
                        }}
                        placeholder="Société"
                      />
                      <Button
                        plain
                        onClick={() => onRemoveNewRow(row.tempId)}
                        aria-label="Annuler l'ajout du contrat"
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-1.5 align-top">
                    <input
                      type="text"
                      value={row.shortName}
                      onChange={(e) =>
                        onNewRowChange(row.tempId, {
                          shortName: e.target.value,
                        })
                      }
                      maxLength={50}
                      className={inputClass(true)}
                      placeholder="Ex: GO"
                    />
                  </td>
                  <td
                    className="p-1.5 align-top text-xs text-zinc-400"
                    title="Enregistrez ce contrat pour pouvoir gérer référent et équipes."
                  >
                    —
                  </td>
                  {showGroupement && (
                    <td className="p-1.5 align-top">
                      <Select
                        flat
                        options={groupOptions}
                        value={row.groupId ? [String(row.groupId)] : []}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          onNewRowChange(row.tempId, {
                            groupId: val && val !== "0" ? Number(val) : null,
                          });
                        }}
                        placeholder=""
                      />
                    </td>
                  )}
                  <td
                    className="border-l-2 border-zinc-300 p-1.5 align-top text-xs text-zinc-400 dark:border-zinc-700"
                    title="Enregistrez ce contrat pour pouvoir gérer référent et équipes."
                  >
                    —
                  </td>
                  <td
                    className="p-1.5 align-top text-xs text-zinc-400"
                    title="Enregistrez ce contrat pour pouvoir gérer référent et équipes."
                  >
                    —
                  </td>
                  {showParentSelect && (
                    <td className="p-1.5 align-top">
                      <Select
                        flat
                        options={parentOptions(null)}
                        value={[String(row.parentContractId ?? 0)]}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          onNewRowChange(row.tempId, {
                            parentContractId:
                              val && val !== "0" ? Number(val) : null,
                          });
                        }}
                        placeholder="Donneur d'ordre"
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type SubcontractorGroup = {
  parentContractId: number;
  title: string;
  rows: WorkContractRow[];
  newRows: NewContractRow[];
};

type SubcontractorsTableProps = {
  groups: SubcontractorGroup[];
  companyOptions: SelectOption[];
  companyLocked: boolean;
  getDraft: (contract: WorkContractRow) => ContractDraft;
  isDirtyRow: (contract: WorkContractRow) => boolean;
  onFieldChange: (
    contract: WorkContractRow,
    patch: Partial<ContractDraft>,
  ) => void;
  onNewRowChange: (tempId: string, patch: NewContractPatch) => void;
  onRemoveNewRow: (tempId: string) => void;
  /** Left out when contracts can no longer be added: hides the buttons. */
  onAddRow?: (parentContractId: number) => void;
  onManageTeams: (contract: WorkContractRow) => void;
  onSetReferent: (contract: WorkContractRow) => void;
  onRemoveReferent: (contract: WorkContractRow) => void;
  /** After mise en service: the referent is deactivated (and replaced), not removed. */
  onDeactivateReferent?: (contract: WorkContractRow) => void;
  onDeleteHistory?: (removal: ReferentHistoryRemoval) => void;
  onInterrupt: (contract: WorkContractRow) => void;
  onDelete: (contract: WorkContractRow) => void;
  deleteRefusal: (contract: WorkContractRow) => string | undefined;
};

/**
 * One combined table for every donneur-d'ordre group whose rang 1 contract
 * is resolved and active: a single column header instead of one table — and
 * header — per group, the donneur d'ordre becoming a vertical cell spanning
 * its group's rows (same layout as the lots/prestations and bâtiments/zones
 * tables, screens 904/905). Groups without a resolved active parent (orphan
 * or interrupted) keep their own `ContractTable` with its selector instead
 * — see `specialParentIds`.
 *
 * A plain `<table>` (not `GroupedTable`) on purpose: `GroupedTable`'s own
 * root wrapper always sets `overflow-auto`, which makes it — not the
 * panel's scroll area — the nearest scrolling ancestor for any
 * `position: sticky` descendant, so the header never stuck.
 */
function SubcontractorsTable({
  groups,
  companyOptions,
  companyLocked,
  getDraft,
  isDirtyRow,
  onFieldChange,
  onNewRowChange,
  onRemoveNewRow,
  onAddRow,
  onManageTeams,
  onSetReferent,
  onRemoveReferent,
  onDeactivateReferent,
  onDeleteHistory,
  onInterrupt,
  onDelete,
  deleteRefusal,
}: SubcontractorsTableProps) {
  const { headerRef, style } = useStickyHeaderOffsets();
  return (
    <div className={contractsTableFrameClass} style={style}>
      <table className="w-full table-fixed border-collapse text-xs">
        <colgroup>
          <col style={{ width: columnWidths.donneurOrdre }} />
          <col style={{ width: columnWidths.designation }} />
          <col style={{ width: columnWidths.abbreviation }} />
          <col style={{ width: columnWidths.referentWithoutGroupement }} />
          <col style={{ width: columnWidths.team }} />
          <col style={{ width: columnWidths.members }} />
        </colgroup>
        <thead ref={headerRef}>
          {/* Même apparence à deux lignes que le tableau contrats rang 1 :
              groupe "Société contractante" (Désignation/Abrév./Référent
              contrat) et groupe "Équipes" (Désignation/Membres). */}
          <tr className="bg-zinc-50 text-left dark:bg-zinc-900">
            <th
              rowSpan={2}
              className={cn(
                headFirstRowCellClass,
                "align-bottom !whitespace-normal",
              )}
            >
              Donneur d'ordre
            </th>
            <th className={headGroupCellClass} colSpan={3}>
              Société contractante
            </th>
            <th
              className={cn(headGroupCellClass, teamsSeparatorClass)}
              colSpan={2}
            >
              Équipes
            </th>
          </tr>
          <tr className="bg-zinc-50 text-left dark:bg-zinc-900">
            <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
              Désignation
            </th>
            <th
              className={cn(stickyHeadSecondRowClass, headCellClass)}
              title="Abréviation"
            >
              Abrév.
            </th>
            <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
              Référent contrat
            </th>
            <th
              className={cn(
                stickyHeadSecondRowClass,
                headCellClass,
                teamsSeparatorClass,
              )}
            >
              Désignation
            </th>
            <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
              Membres
            </th>
          </tr>
        </thead>
        {groups.map((group) => (
          <SubcontractorGroupBody
            key={group.parentContractId}
            title={group.title}
            rows={group.rows}
            newRows={group.newRows}
            companyOptions={companyOptions}
            companyLocked={companyLocked}
            getDraft={getDraft}
            isDirtyRow={isDirtyRow}
            onFieldChange={onFieldChange}
            onNewRowChange={onNewRowChange}
            onRemoveNewRow={onRemoveNewRow}
            onAddRow={
              isNotNil(onAddRow)
                ? () => onAddRow(group.parentContractId)
                : undefined
            }
            onManageTeams={onManageTeams}
            onSetReferent={onSetReferent}
            onRemoveReferent={onRemoveReferent}
            onDeactivateReferent={onDeactivateReferent}
            onDeleteHistory={onDeleteHistory}
            onInterrupt={onInterrupt}
            onDelete={onDelete}
            deleteRefusal={deleteRefusal}
          />
        ))}
      </table>
    </div>
  );
}

type SubcontractorGroupBodyProps = {
  title: string;
  rows: WorkContractRow[];
  newRows: NewContractRow[];
  companyOptions: SelectOption[];
  companyLocked: boolean;
  getDraft: (contract: WorkContractRow) => ContractDraft;
  isDirtyRow: (contract: WorkContractRow) => boolean;
  onFieldChange: (
    contract: WorkContractRow,
    patch: Partial<ContractDraft>,
  ) => void;
  onNewRowChange: (tempId: string, patch: NewContractPatch) => void;
  onRemoveNewRow: (tempId: string) => void;
  /** Left out when contracts can no longer be added: hides the button. */
  onAddRow?: () => void;
  onManageTeams: (contract: WorkContractRow) => void;
  onSetReferent: (contract: WorkContractRow) => void;
  onRemoveReferent: (contract: WorkContractRow) => void;
  /** After mise en service: the referent is deactivated (and replaced), not removed. */
  onDeactivateReferent?: (contract: WorkContractRow) => void;
  onDeleteHistory?: (removal: ReferentHistoryRemoval) => void;
  onInterrupt: (contract: WorkContractRow) => void;
  onDelete: (contract: WorkContractRow) => void;
  deleteRefusal: (contract: WorkContractRow) => string | undefined;
};

/**
 * One donneur-d'ordre's sous-traitants as a `<tbody>` : its name spans every
 * row of the group (`leading`, first row only) plus the trailing "Ajouter"
 * row, same rowSpan layout as the lot/prestation table (screen 905). Each
 * contract contributes one row per team (société-side cells rowSpan across
 * them) — same split as the rang 1/2 `ContractTable`, which this also
 * mirrors for where action buttons live (contract-level in the Désignation
 * (société) cell, team-level in the Désignation (équipe) cell — no separate
 * Actions column).
 */
function SubcontractorGroupBody({
  title,
  rows,
  newRows,
  companyOptions,
  companyLocked,
  getDraft,
  isDirtyRow,
  onFieldChange,
  onNewRowChange,
  onRemoveNewRow,
  onAddRow,
  onManageTeams,
  onSetReferent,
  onRemoveReferent,
  onDeactivateReferent,
  onDeleteHistory,
  onInterrupt,
  onDelete,
  deleteRefusal,
}: SubcontractorGroupBodyProps) {
  const contractSlots = rows.flatMap((contract) => {
    const teamRows: (Team | null)[] =
      contract.teams.length > 0 ? contract.teams : [null];
    return teamRows.map((team, index) => ({
      contract,
      team,
      isFirstTeamRow: index === 0,
      teamRowCount: teamRows.length,
    }));
  });
  const rowCount = contractSlots.length + newRows.length;
  const totalRows = Math.max(1, rowCount) + 1;

  const leadingCell = (
    <td rowSpan={totalRows} className="align-top">
      {/* Row header of the group: stays under the column headers while its
          rows scroll (screen-design.md, two-level tables). */}
      <div
        className={cn(stickyRowHeaderContentClass, "flex justify-center pt-1")}
      >
        <span
          className="whitespace-nowrap text-xs font-medium text-zinc-700 dark:text-zinc-300"
          style={{ writingMode: "vertical-rl" }}
          title={title}
        >
          {title}
        </span>
      </div>
    </td>
  );

  return (
    // Séparation marquée entre les tableaux empilés de donneurs d'ordre
    // successifs : bordure plus visible qu'un simple trait fin.
    <tbody className="border-b-2 border-zinc-300 last:border-0 dark:border-zinc-600">
      {rowCount === 0 && (
        <tr>
          {leadingCell}
          <td colSpan={5} className="p-1.5 text-zinc-400">
            Aucun contrat sous-traité
          </td>
        </tr>
      )}

      {contractSlots.map((slot, flatIndex) => {
        const { contract, team, isFirstTeamRow, teamRowCount } = slot;
        const draft = getDraft(contract);
        const dirty = isDirtyRow(contract);
        return (
          <tr
            key={team ? `${contract.id}-${team.id}` : contract.id}
            className={cn(
              "border-t border-zinc-100 dark:border-zinc-800",
              dirty && "bg-amber-50 dark:bg-amber-900/20",
            )}
          >
            {flatIndex === 0 && leadingCell}
            {isFirstTeamRow && (
              <>
                <td rowSpan={teamRowCount} className="p-1.5 align-top">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <PerformerSelect
                        options={companyOptions}
                        value={draft.performerId}
                        locked={companyLocked}
                        onChange={(performerId) =>
                          onFieldChange(contract, { performerId })
                        }
                      />
                      {!contract.isActive && (
                        <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          Interrompu
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {contract.isActive && (
                        <Button
                          plain
                          onClick={() => onInterrupt(contract)}
                          aria-label="Interrompre le contrat"
                        >
                          <ProhibitIcon className="size-4" />
                        </Button>
                      )}
                      <Button
                        plain
                        onClick={() => onDelete(contract)}
                        disabled={isNotNil(deleteRefusal(contract))}
                        aria-label="Supprimer"
                        title={deleteRefusal(contract)}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </div>
                  </div>
                </td>
                <td rowSpan={teamRowCount} className="p-1.5 align-top">
                  <input
                    type="text"
                    value={draft.shortName}
                    onChange={(e) =>
                      onFieldChange(contract, { shortName: e.target.value })
                    }
                    maxLength={50}
                    className={inputClass(dirty)}
                    placeholder="Ex: GO"
                  />
                </td>
                <td rowSpan={teamRowCount} className="p-1.5 align-top">
                  <ReferentCell
                    referent={contract.referent}
                    history={contract.referentHistory}
                    onDeleteHistory={historyRemover(
                      onDeleteHistory,
                      contract.id,
                    )}
                    actions={
                      <ReferentActions
                        contract={contract}
                        onSetReferent={onSetReferent}
                        onRemoveReferent={onRemoveReferent}
                        onDeactivateReferent={onDeactivateReferent}
                      />
                    }
                  />
                </td>
              </>
            )}
            <td className="border-l-2 border-zinc-300 p-1.5 align-top dark:border-zinc-700">
              <div className="flex items-center justify-between gap-1">
                {team ? (
                  <span
                    className="min-w-0 truncate text-xs font-medium text-zinc-700 dark:text-zinc-300"
                    title={team.name}
                  >
                    {team.name}
                  </span>
                ) : (
                  <span className="text-zinc-400">-</span>
                )}
                {isFirstTeamRow && (
                  <Button
                    plain
                    onClick={() => onManageTeams(contract)}
                    aria-label="Gérer les équipes"
                    className="shrink-0"
                  >
                    <UsersThreeIcon className="size-4" />
                  </Button>
                )}
              </div>
            </td>
            <td className="p-1.5 align-top">
              {team ? (
                <TeamMembersCell
                  team={team}
                  onDeleteHistory={historyRemover(
                    onDeleteHistory,
                    contract.id,
                    team.name,
                  )}
                />
              ) : (
                "-"
              )}
            </td>
          </tr>
        );
      })}

      {newRows.map((row, index) => (
        <tr
          key={row.tempId}
          ref={revealOnMount}
          className="border-t border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20"
        >
          {contractSlots.length === 0 && index === 0 && leadingCell}
          <td className="p-1.5 align-top">
            <div className="flex items-center gap-1">
              <Select
                flat
                options={companyOptions}
                value={row.performerId ? [String(row.performerId)] : []}
                onValueChange={(details) => {
                  const val = details.value[0];
                  onNewRowChange(row.tempId, {
                    performerId: val ? Number(val) : 0,
                  });
                }}
                placeholder="Société"
              />
              <Button
                plain
                onClick={() => onRemoveNewRow(row.tempId)}
                aria-label="Annuler l'ajout du contrat"
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          </td>
          <td className="p-1.5 align-top">
            <input
              type="text"
              value={row.shortName}
              onChange={(e) =>
                onNewRowChange(row.tempId, { shortName: e.target.value })
              }
              maxLength={50}
              className={inputClass(true)}
              placeholder="Ex: GO"
            />
          </td>
          <td
            className="p-1.5 align-top text-xs text-zinc-400"
            title="Enregistrez ce contrat pour pouvoir gérer référent et équipes."
          >
            —
          </td>
          <td
            className="border-l-2 border-zinc-300 p-1.5 align-top text-xs text-zinc-400 dark:border-zinc-700"
            title="Enregistrez ce contrat pour pouvoir gérer référent et équipes."
          >
            —
          </td>
          <td
            className="p-1.5 align-top text-xs text-zinc-400"
            title="Enregistrez ce contrat pour pouvoir gérer référent et équipes."
          >
            —
          </td>
        </tr>
      ))}

      {isNotNil(onAddRow) && (
        <tr>
          <td colSpan={5} className="p-1.5">
            <button
              type="button"
              onClick={onAddRow}
              className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            >
              <PlusIcon className="size-3.5" />
              Ajouter un contrat de sous-traitance
            </button>
          </td>
        </tr>
      )}
    </tbody>
  );
}

type ReferentActionsProps = {
  contract: WorkContractRow;
  onSetReferent: (contract: WorkContractRow) => void;
  onRemoveReferent: (contract: WorkContractRow) => void;
  onDeactivateReferent?: (contract: WorkContractRow) => void;
};

function ReferentActions({
  contract,
  onSetReferent,
  onRemoveReferent,
  onDeactivateReferent,
}: ReferentActionsProps) {
  const referent = contract.referent;
  return (
    <>
      <Button
        plain
        onClick={() => onSetReferent(contract)}
        aria-label="Définir le référent"
      >
        <UserPlusIcon className="size-4" />
      </Button>
      {isNotNil(referent) &&
        (isNil(onDeactivateReferent) ? (
          <Button
            plain
            onClick={() => onRemoveReferent(contract)}
            aria-label="Retirer le référent"
          >
            <TrashIcon className="size-3.5" />
          </Button>
        ) : (
          referent.isActive && (
            <Button
              plain
              onClick={() => onDeactivateReferent(contract)}
              aria-label="Désactiver le référent"
            >
              <ProhibitIcon className="size-3.5" />
            </Button>
          )
        ))}
    </>
  );
}

export type TeamMembersCellProps = {
  team: Team;
  onDeleteHistory?: (entry: ReferentHistoryEntry) => void;
};

/**
 * A team's members, one per line, in the "Membres" column: the team's
 * referents first, most recent at the top (the current one highlighted, then
 * the former ones « Réf. jusqu'au <date> »), then the other members (PO, N28,
 * 03/11).
 */
export function TeamMembersCell({
  team,
  onDeleteHistory,
}: TeamMembersCellProps) {
  const formerIds = new Set(team.referentHistory.map((e) => e.employeeId));
  const current = team.members.filter((m) => m.isReferent);
  const others = team.members.filter(
    (m) => !m.isReferent && !formerIds.has(m.employeeId),
  );
  if (team.members.length === 0 && team.referentHistory.length === 0) {
    return <span className="text-xs text-zinc-400">Aucun membre</span>;
  }
  const memberLine = (member: TeamMember) => (
    <span key={member.id} className="flex max-w-full items-center gap-1">
      <span
        className={cn(
          "min-w-0 truncate text-xs text-zinc-600 dark:text-zinc-400",
          member.isReferent &&
            "rounded-full border border-blue-400 px-1.5 text-blue-700 dark:border-blue-500 dark:text-blue-300",
          !member.isActive && "text-zinc-400 dark:text-zinc-500",
        )}
        title={memberName(member)}
      >
        {memberName(member)}
      </span>
      {!member.isActive && (
        <DeactivatedLabel
          title={
            member.isReferent
              ? "Désactivé : désignez un autre référent"
              : undefined
          }
        />
      )}
    </span>
  );
  return (
    <div className="flex flex-col items-start gap-0.5">
      {current.map(memberLine)}
      {team.referentHistory.map((entry) => {
        const name = personName(entry.employeeId, entry.employee);
        const day = replacementDay(entry.replacedAt);
        return (
          <FormerReferent
            key={entry.id}
            name={name}
            untilLabel={`Réf. jusqu'au ${day}`}
            title={`${name}, référent de l'équipe jusqu'au ${day}`}
            onDelete={onDeleteHistory && (() => onDeleteHistory(entry))}
          />
        );
      })}
      {others.map(memberLine)}
    </div>
  );
}
